import { useState } from 'react';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [statusMsg, setStatusMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setStatus('success');
      setStatusMsg('Inscrição realizada com sucesso!');
      setEmail('');
      return;
    }

    setStatus('loading');
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      await fetch('https://readdy.ai/api/form/d8voh70bhec1he3opdt0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setStatus('success');
      setStatusMsg('Inscrição realizada com sucesso!');
      setEmail('');
    } catch {
      setStatus('error');
      setStatusMsg('Erro ao inscrever. Tente novamente.');
    }
  };

  const col1 = categories.slice(0, 4);
  const col2 = categories.slice(4, 8);
  const col3 = categories.slice(8, 12);

  return (
    <footer className="bg-foreground-950 pt-16 md:pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Top area */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-12">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-background-50/10 flex items-center justify-center">
              <i className="ri-database-2-line text-background-50/80 text-lg"></i>
            </div>
            <span className="text-background-50 font-bold text-sm">
              cssbuyspreadsheet<span className="text-background-50/40">.blog</span>
            </span>
          </div>
          <p className="text-background-50/40 text-sm max-w-xs">
            O maior diretório de cssbuy spreadsheet do Brasil. Atualizado diariamente para a comunidade.
          </p>
        </div>

        {/* Navigation Columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {/* Col 1 - Roupas */}
          <div>
            <h4 className="text-background-50 font-semibold text-sm mb-4">Roupas</h4>
            <ul className="space-y-2.5">
              {col1.map((cat) => (
                <li key={cat.seq}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-background-50/50 hover:text-background-50/80 text-sm transition-colors duration-200"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 2 - Acessórios */}
          <div>
            <h4 className="text-background-50 font-semibold text-sm mb-4">Acessórios</h4>
            <ul className="space-y-2.5">
              {col2.map((cat) => (
                <li key={cat.seq}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-background-50/50 hover:text-background-50/80 text-sm transition-colors duration-200"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 - Mais */}
          <div>
            <h4 className="text-background-50 font-semibold text-sm mb-4">Mais</h4>
            <ul className="space-y-2.5">
              {col3.map((cat) => (
                <li key={cat.seq}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-background-50/50 hover:text-background-50/80 text-sm transition-colors duration-200"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4 - Copyright + Newsletter */}
          <div className="col-span-2 md:col-span-1">
            <div className="mb-6">
              <p className="text-background-50/70 text-lg font-semibold font-serif mb-1">
                &copy; CSSBuy Spreadsheet 2026
              </p>
              <p className="text-background-50/30 text-sm font-serif">All rights reserved</p>
            </div>

            {/* Newsletter */}
            <div>
              <p className="text-background-50/40 text-xs mb-3 leading-relaxed">
                Receba atualizações e novos produtos
                <br />
                diretamente no seu e-mail.
              </p>
              <form onSubmit={handleSubmit} data-readdy-form="true" className="flex gap-2">
                <div className="visitor-field-wrap">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                </div>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  className="flex-1 px-4 py-2.5 rounded-full bg-background-50/10 border border-background-50/15 text-background-50 text-sm placeholder:text-background-50/30 focus:outline-none focus:border-background-50/40 transition-colors duration-200"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="px-5 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {status === 'loading' ? '...' : 'Subscrever'}
                </button>
              </form>
              {status !== 'idle' && (
                <p className={`text-xs mt-2 ${status === 'success' ? 'text-accent-400' : 'text-red-400'}`}>
                  {statusMsg}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-background-50/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <a href="/guia-de-compras" className="text-background-50/30 hover:text-background-50/60 text-xs transition-colors duration-200">
              Guia de Compras
            </a>
            <a href="/" className="text-background-50/30 hover:text-background-50/60 text-xs transition-colors duration-200">
              Início
            </a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-background-50/30 hover:text-background-50/60 text-xs transition-colors duration-200">
              kaspreadsheet.com
            </a>
          </div>
          <p className="text-background-50/20 text-xs">
            cssbuyspreadsheet.blog — Diretório independente de produtos
          </p>
        </div>
      </div>
    </footer>
  );
}